<script setup>
  import { computed, ref, watch, nextTick } from "vue";
  import { storeToRefs } from "pinia";
  import { Label } from "@/components/ui/label";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { RadioGroup, RadioGroupItemIcon } from "@/components/ui/radio-group";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { PRESET_VALUES, PRESET_OPTIONS, WIZARD_FIELDS, buildPromptFromValues } from "./reportWizardConfig";

  const emit = defineEmits(["generate"]);

  const { currentReport } = storeToRefs(useReportsV2Store());
  const reportKind = computed(() => (currentReport.value?.master_report?.is_qualitative ? "qualitative" : "quantitative"));
  const presetOptions = computed(() => PRESET_OPTIONS[reportKind.value]);

  const activePreset = ref(null);
  const formValues = ref({ ...PRESET_VALUES.quantitative.empty });
  const presetLabelRef = ref(null);

  watch(activePreset, (preset) => {
    if (preset) {
      formValues.value = { ...PRESET_VALUES[reportKind.value][preset] };
      nextTick(() => {
        const el = presetLabelRef.value?.$el || presetLabelRef.value;
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  });

  function handleGenerate() {
    const prompt = buildPromptFromValues(formValues.value);
    if (prompt) {
      emit("generate", prompt);
    }
  }

  defineExpose({ activePreset, handleGenerate });
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="flex-1 overflow-y-auto">
      <hr class="my-8 border-border" />
      <p class="text-sm font-bold">
        You will now have to provide some instructions to help AI understand your expectations for this report.
        <br />
        You can provide all instructions manually, or use some preset instructions.
      </p>

      <!-- Preset selector -->
      <Label
        ref="presetLabelRef"
        class="mt-6 mb-2 block">Select preset instructions</Label>
      <RadioGroup
        v-model="activePreset"
        class="grid grid-cols-3 gap-3">
        <RadioGroupItemIcon
          v-for="option in presetOptions"
          :key="option.value"
          :value="option.value"
          :label="option.label"
          :description="option.description"
          class="h-full [&>div:first-child]:hidden" />
      </RadioGroup>

      <!-- Form fields (shown only when a preset is selected) -->
      <template v-if="activePreset">
        <hr class="my-8 border-border" />
        <p class="text-sm font-bold pb-8">
          Please answer the questions below and then click Generate with AI. All questions are optional, just provide any relevant information you have in mind to get the best results.
        </p>
        <div class="space-y-5">
          <div
            v-for="(field, index) in WIZARD_FIELDS"
            :key="field.key"
            class="space-y-1.5">
            <div class="flex items-center gap-2">
              <span class="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">{{ index + 1 }}</span>
              <Label class="text-sm font-medium">{{ field.label }}</Label>
            </div>
            <p class="text-xs text-muted-foreground pl-8">{{ field.subtitle }}</p>
            <div class="pl-8">
              <Textarea
                v-if="field.type === 'textarea'"
                v-model="formValues[field.key]"
                rows="2"
                class="text-sm max-h-60" />
              <Input
                v-else
                v-model="formValues[field.key]"
                type="number"
                min="0"
                step="5"
                class="text-sm w-28" />
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
